import React, { useEffect, useRef } from 'react';

interface ScreenplayPaginationProps {
  children: React.ReactNode;
}

const A4_HEIGHT = 1123; // pixels
const PAGE_MARGIN_TOP = 96; // 1 inch
const PAGE_MARGIN_BOTTOM = 96; // 1 inch
const USABLE_HEIGHT = A4_HEIGHT - PAGE_MARGIN_TOP - PAGE_MARGIN_BOTTOM; // 931px
const PAGE_GAP = 32; // Gap between pages in pixels

/**
 * ScreenplayPagination component that displays content in A4-sized pages
 * 
 * This component measures content and creates visual page boundaries:
 * - Each page is A4 sized (794px × 1123px)
 * - Content flows naturally with page breaks inserted at appropriate intervals
 * - Blocks avoid breaking across pages using CSS
 * - Pages appear as separate white sheets with gaps and shadows
 */
export const ScreenplayPagination: React.FC<ScreenplayPaginationProps> = ({ children }) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    // Function to add page break indicators based on content height
    const addPageBreaks = () => {
      if (!wrapper) return;

      // Remove existing page break divs
      const existingBreaks = wrapper.querySelectorAll('.page-break-spacer');
      existingBreaks.forEach(br => br.remove());

      // Get all blocks - look for the space-y-3 container first, then its children
      const blockContainer = wrapper.querySelector('.space-y-3');
      if (!blockContainer) return;

      const blocks = blockContainer.querySelectorAll(':scope > .group.relative');
      if (blocks.length === 0) return;

      let currentPageHeight = 0;

      blocks.forEach((block, index) => {
        const blockEl = block as HTMLElement;
        const rect = blockEl.getBoundingClientRect();
        
        // Calculate actual space occupied including margins
        let actualSpace: number;
        if (index === 0) {
          // First block - just use its height
          actualSpace = rect.height;
        } else {
          // Get previous block's bottom position
          const prevBlock = blocks[index - 1] as HTMLElement;
          const prevRect = prevBlock.getBoundingClientRect();
          // Space = distance from previous block's bottom to current block's bottom
          actualSpace = rect.bottom - prevRect.bottom;
        }
        
        // Check if adding this block would exceed the usable page height
        if (currentPageHeight + actualSpace > USABLE_HEIGHT && currentPageHeight > 0) {
          // Insert a page break spacer before this block
          const spacer = document.createElement('div');
          spacer.className = 'page-break-spacer';
          spacer.style.height = `${PAGE_GAP}px`;
          spacer.style.width = 'calc(100% + 240px)'; // Extend into margins (144px + 96px)
          spacer.style.background = '#f3f4f6';
          spacer.style.marginLeft = '-144px'; // Extend into left margin
          spacer.style.marginRight = '-96px'; // Extend into right margin
          spacer.style.marginTop = '0';
          spacer.style.marginBottom = '0';
          spacer.style.boxSizing = 'border-box';
          spacer.style.position = 'relative';
          spacer.style.borderTop = '2px solid #e5e7eb';
          spacer.style.borderBottom = '2px solid #e5e7eb';
          
          blockEl.parentElement?.insertBefore(spacer, blockEl);
          
          // Reset height counter for new page
          currentPageHeight = actualSpace;
        } else {
          currentPageHeight += actualSpace;
        }
      });
    };

    // Add page breaks after content renders - use requestAnimationFrame for proper timing
    let timeoutId: NodeJS.Timeout;
    const rafId = requestAnimationFrame(() => {
      // Wait for next paint, then use longer timeout to ensure content is measured
      timeoutId = setTimeout(addPageBreaks, 300);
    });

    // Re-calculate on window resize
    const handleResize = () => {
      requestAnimationFrame(() => {
        setTimeout(addPageBreaks, 300);
      });
    };
    window.addEventListener('resize', handleResize);

    // Observer for content changes
    const observer = new MutationObserver(() => {
      requestAnimationFrame(() => {
        setTimeout(addPageBreaks, 300);
      });
    });

    observer.observe(wrapper, {
      childList: true,
      subtree: true,
      attributes: false,
    });

    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      observer.disconnect();
    };
  }, [children]);

  return (
    <div className="screenplay-page-wrapper">
      <div ref={wrapperRef} className="screenplay-page screenplay-continuous">
        {children}
      </div>
    </div>
  );
};

